import type {
  FrontendUserActionName,
  FrontendUserActionSurface,
} from "@repo/domain-events";

export type UserActionMutationIdentity =
  `${FrontendUserActionSurface}:${FrontendUserActionName}`;

export type UserActionReconciliationChannel =
  | "browser-focus-refetch"
  | "caller-callback"
  | "domain-query-invalidation"
  | "execution-operation-stream"
  | "local-state"
  | "none"
  | "query-invalidation"
  | "query-refetch"
  | "query-write"
  | "status-polling"
  | "tanstack-db-write"
  | "tanstack-db-polling";

export interface UserActionCacheOwnership {
  readonly authoritativeSource: "server" | "server-and-chain";
  readonly directQueryUpdates: readonly string[];
  readonly optimisticQueryUpdates: readonly string[];
  readonly invalidates: readonly string[];
  readonly refetches: readonly string[];
  readonly tanstackDbWrites: readonly string[];
  readonly reconcilesVia: readonly UserActionReconciliationChannel[];
}

/**
 * Cache ownership for every React Query mutation carrying a user-action
 * identity. Targets name the actual cache seam used by the mutation rather
 * than promising a broader product refresh.
 */
export const USER_ACTION_CACHE_OWNERSHIP = {
  "earn_accumulator:accumulator.complete_setup": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [
      "platformApi.accumulatorList",
      "platformApi.accumulatorSummaryById",
    ],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "earn_accumulator:accumulator.create": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [
      "platformApi.accumulatorList",
      "platformApi.accumulatorSummaryById",
    ],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "receipts:receipt.share": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["platformApi.receiptById"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "settings:settings.display_preference.save": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["walletIdentity.walletIdentities"],
    refetches: ["currentUser.displayProfile"],
    tanstackDbWrites: [],
    reconcilesVia: ["query-refetch", "query-invalidation"],
  },
  "settings:settings.swap_preferences.save": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: ["settings.userSwapPreferences"],
    tanstackDbWrites: [],
    reconcilesVia: ["query-refetch"],
  },
  "settings:settings.swap_preferences.reset": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: ["settings.userSwapPreferences"],
    tanstackDbWrites: [],
    reconcilesVia: ["query-refetch"],
  },
  "settings:settings.feature_code.redeem": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: ["settings.userFeatureFlags"],
    tanstackDbWrites: [],
    reconcilesVia: ["query-refetch"],
  },
  "settings:settings.oauth.drip.connect": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: ["settings.verifiedDrip"],
    tanstackDbWrites: [],
    reconcilesVia: ["browser-focus-refetch"],
  },
  "settings:settings.oauth.drip.disconnect": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: ["settings.verifiedDrip"],
    tanstackDbWrites: [],
    reconcilesVia: ["query-refetch"],
  },
  "asset:watchlist.add": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["supplied-auth-scoped-watchlist-query"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "asset:watchlist.remove": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["supplied-auth-scoped-watchlist-query"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "portfolio:portfolio.deposit.execute": {
    authoritativeSource: "server-and-chain",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["none"],
  },
  // Mints a single-use provider URL; the click handler navigates the
  // pre-claimed tab and the deposit itself reconciles later through the
  // receive-panel address watch, not this mutation.
  "portfolio:portfolio.deposit.exchange_onramp_url": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback"],
  },
  // Moves idle investing-account funds into the portfolio: invalidates the
  // deposit-modal queries plus the shared balance-affecting set, and the
  // Solana leg polls the lending-aware transfer status to a terminal state.
  "portfolio:portfolio.deposit.glider_balance_transfer": {
    authoritativeSource: "server-and-chain",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["walletExecutionDeposit"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation", "status-polling"],
  },
  "dashboard:watchlist.add": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: ["dashboard.watchlist"],
    invalidates: ["dashboard.watchlist"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "dashboard:watchlist.remove": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: ["dashboard.watchlist"],
    invalidates: ["dashboard.watchlist"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "dashboard:watchlist.reorder": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: ["dashboard.watchlist"],
    invalidates: ["dashboard.watchlist"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "portfolio:portfolio.deposit.track": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["execution-operation-stream"],
  },
  "portfolio:portfolio.withdraw.execute": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["execution-operation-stream"],
  },
  "portfolio:portfolio.transfer.execute": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["execution-operation-stream"],
  },
  // The five lifecycle mutations below reconcile through the injected
  // refreshPortfolioDetail callback, whose sole production binding
  // (portfolio-page-screen) invalidates the detail bootstrap query and the
  // portfolio-domain detail collection before re-seeding it.
  "portfolio:portfolio.rebalance.run": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["portfolio.detailBootstrap", "portfolioDomain.detail"],
    refetches: ["portfolio.rebalanceStatus"],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback", "execution-operation-stream"],
  },
  "portfolio:portfolio.schedule.pause": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["portfolio.detailBootstrap", "portfolioDomain.detail"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback"],
  },
  "portfolio:portfolio.schedule.resume": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["portfolio.detailBootstrap", "portfolioDomain.detail"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback"],
  },
  "portfolio:portfolio.schedule.set_cadence": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["portfolio.detailBootstrap", "portfolioDomain.detail"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback"],
  },
  "portfolio:portfolio.schedule.start": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["portfolio.detailBootstrap", "portfolioDomain.detail"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback"],
  },
  "portfolio:portfolio.settings.save": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["portfolio.detailBootstrap", "portfolioDomain.detail"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback"],
  },
  "portfolio:portfolio.swap_preferences.save": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback"],
  },
  "portfolio:portfolio.swap_preferences.reset": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback"],
  },
  "portfolio:portfolio.archive": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["portfolio.detailBootstrap", "portfolioDomain.detail"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback"],
  },
  "portfolio:portfolio.unarchive": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["portfolio.detailBootstrap", "portfolioDomain.detail"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["caller-callback"],
  },
  "portfolio:portfolio.approval.decide": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [
      "agentControlPlane.all",
      "dashboard.controlPlane",
      "dashboard.portfolioInstances",
      "dashboard.reviewQueue",
      "portfolio.approvals",
      "portfolio.contexts",
      "portfolio.controlPlane",
      "portfolio.events",
      "portfolio.portfolioInstances",
      "portfolio.runs",
      "portfolio.status",
    ],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation", "caller-callback"],
  },
  "agent_control_plane:agent.memory.create": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [
      "agentControlPlane.assistantMemories",
      "agentControlPlane.assistantMemoryRelated",
    ],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "agent_control_plane:agent.memory.update": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["agentControlPlane.assistantMemoryRelated"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "agent_control_plane:agent.memory.delete": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["agentControlPlane.assistantMemoryRelated"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "agent_control_plane:agent.prompt.submit": {
    authoritativeSource: "server",
    directQueryUpdates: ["agentControlPlane.assistantConversation"],
    optimisticQueryUpdates: [],
    invalidates: [
      "agentControlPlane.assistantMemoryRelated",
      "agentControlPlane.assistantTasks",
      "agentControlPlane.assistantWorkspace",
      "agentControlPlane.assistantWorkspaces",
    ],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-write", "query-invalidation"],
  },
  "agent_control_plane:agent.workspace.create": {
    authoritativeSource: "server",
    directQueryUpdates: ["agentControlPlane.assistantWorkspaceById"],
    optimisticQueryUpdates: [],
    invalidates: ["agentControlPlane.assistantWorkspaces"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-write", "query-invalidation"],
  },
  "agent_control_plane:agent.onboarding.start": {
    authoritativeSource: "server",
    directQueryUpdates: [
      "agentControlPlane.assistantConversation",
      "agentControlPlane.assistantWorkspaceById",
    ],
    optimisticQueryUpdates: [],
    invalidates: ["agentControlPlane.assistantWorkspaces"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-write", "query-invalidation"],
  },
  "agent_control_plane:agent.workspace.update": {
    authoritativeSource: "server",
    directQueryUpdates: ["agentControlPlane.assistantWorkspaceById"],
    optimisticQueryUpdates: [],
    invalidates: ["agentControlPlane.assistantWorkspaces"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-write", "query-invalidation"],
  },
  "agent_control_plane:agent.portfolio_construction.start": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["none"],
  },
  "agent_control_plane:agent.approval.decide": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [
      "agentControlPlane.all",
      "dashboard.controlPlane",
      "dashboard.reviewQueue",
      "portfolio.approvals",
      "portfolio.controlPlane",
      "portfolio.events",
      "portfolio.runs",
      "portfolio.status",
    ],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["query-invalidation"],
  },
  "portfolio_construction:portfolio.create": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["portfolioDomain.detail", "portfolioDomain.summaries"],
    refetches: [],
    tanstackDbWrites: [],
    reconcilesVia: ["domain-query-invalidation"],
  },
  "portfolio_construction:portfolio.rebalance.run": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["portfolioDomain.detail", "portfolioDomain.summaries"],
    refetches: ["portfolioCreation.portfolioStatus"],
    tanstackDbWrites: [],
    reconcilesVia: [
      "query-refetch",
      "domain-query-invalidation",
      "status-polling",
    ],
  },
  "investing_account:investing_account.setup": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: ["dashboard.accountPerformanceSeries", "investingAccount.all"],
    refetches: [],
    tanstackDbWrites: ["portfolioDomain.index-and-summaries"],
    reconcilesVia: ["query-invalidation"],
  },
  "start:start.username.claim": {
    authoritativeSource: "server",
    directQueryUpdates: [],
    optimisticQueryUpdates: [],
    invalidates: [],
    refetches: [],
    tanstackDbWrites: ["currentUserIdentity.username"],
    reconcilesVia: ["tanstack-db-write"],
  },
} satisfies Readonly<
  Partial<Record<UserActionMutationIdentity, UserActionCacheOwnership>>
>;

const USER_ACTION_CACHE_OWNERSHIP_BY_IDENTITY: Readonly<
  Partial<Record<UserActionMutationIdentity, UserActionCacheOwnership>>
> = USER_ACTION_CACHE_OWNERSHIP;

export function toUserActionMutationIdentity(
  action: FrontendUserActionName,
  surface: FrontendUserActionSurface,
): UserActionMutationIdentity {
  return `${surface}:${action}`;
}

export function getUserActionCacheOwnership(
  action: FrontendUserActionName,
  surface: FrontendUserActionSurface,
): UserActionCacheOwnership {
  const identity = toUserActionMutationIdentity(action, surface);
  const ownership: UserActionCacheOwnership | undefined =
    USER_ACTION_CACHE_OWNERSHIP_BY_IDENTITY[identity];
  if (!ownership) {
    throw new Error(`Missing user-action cache ownership for ${identity}`);
  }
  return ownership;
}
